import { useState } from 'react';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../tailwind/dialog';
import { Button } from '../../../../tailwind/button';
import { Input } from '../../../../tailwind/input';
import { cn } from '../../../../../lib/utils';
import { MIN_RECHARGE_AMOUNT, MAX_RECHARGE_AMOUNT, QUICK_RECHARGE_AMOUNTS } from '../../../../../utils/wallet/walletConstants';
import {
  calculateGstAmount,
  calculateTotalWithGst,
  formatWalletCurrency,
  parseRechargeCreditsInput,
  validateRechargeCredits,
} from '../../../../../utils/wallet/walletUtils';
import { waBtn, waFocusRing } from '../../../../../pages/whatsapp/utils/whatsappUiTokens';

export function RechargeWalletModal({
  open,
  onOpenChange,
  onProceedToPayment,
  gstNumber: _gstNumber,
  onGstNumberChange: _onGstNumberChange,
  isProcessing = false,
}) {
  const [rechargeAmount, setRechargeAmount] = useState('500');

  const numericAmount = parseInt(rechargeAmount, 10) || 0;
  const rechargeValidation = validateRechargeCredits(numericAmount);
  const isValid = !rechargeValidation.error;
  const showMinError = rechargeAmount !== '' && numericAmount > 0 && numericAmount < MIN_RECHARGE_AMOUNT;
  const showMaxError = numericAmount > MAX_RECHARGE_AMOUNT;
  const gstAmount = calculateGstAmount(numericAmount);
  const totalDue = calculateTotalWithGst(numericAmount);

  const handleAmountChange = (e) => {
    const { value } = parseRechargeCreditsInput(e.target.value);
    setRechargeAmount(value);
  };

  const addAmount = (amount) => {
    const current = parseInt(rechargeAmount, 10) || 0;
    const next = Math.min(current + amount, MAX_RECHARGE_AMOUNT);
    setRechargeAmount(String(next));
  };

  const handlePay = () => {
    const result = validateRechargeCredits(numericAmount);
    if (result.error) return;
    onProceedToPayment({
      baseAmount: result.amount,
      gstAmount: calculateGstAmount(result.amount),
      total: calculateTotalWithGst(result.amount),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md [&>button]:hidden bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800">
        <DialogHeader className="relative">
          <DialogTitle className="text-xl text-left text-gray-900 dark:text-white">Recharge Wallet</DialogTitle>
          <DialogDescription className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-left">
            Enter between {MIN_RECHARGE_AMOUNT.toLocaleString('en-IN')} and {MAX_RECHARGE_AMOUNT.toLocaleString('en-IN')} credits
          </DialogDescription>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute right-0 top-0 h-8 w-8 rounded-md border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <div className="relative">
              <Input
                value={rechargeAmount}
                onChange={handleAmountChange}
                type="text"
                inputMode="numeric"
                min={MIN_RECHARGE_AMOUNT}
                max={MAX_RECHARGE_AMOUNT}
                aria-invalid={showMinError || showMaxError}
                className={cn(
                  'text-lg font-medium h-12 bg-gray-50 dark:bg-slate-950 border-gray-200 dark:border-slate-800 text-gray-900 dark:text-white',
                  waFocusRing,
                  (showMinError || showMaxError) && 'border-red-300 dark:border-red-800'
                )}
                placeholder={String(MIN_RECHARGE_AMOUNT)}
              />
            </div>
            {showMinError && (
              <p className="text-sm text-red-500 dark:text-red-400 font-medium">
                {`Minimum ${MIN_RECHARGE_AMOUNT.toLocaleString('en-IN')} credits required`}
              </p>
            )}
            {showMaxError && (
              <p className="text-sm text-red-500 dark:text-red-400 font-medium">
                {`Maximum ${MAX_RECHARGE_AMOUNT.toLocaleString('en-IN')} credits allowed per recharge`}
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2">
            {QUICK_RECHARGE_AMOUNTS.map((amount) => (
              <Button
                key={amount}
                type="button"
                variant="outline"
                className={cn(waBtn.secondary, 'h-10 text-sm font-medium')}
                onClick={() => addAmount(amount)}
              >
                +{amount.toLocaleString('en-IN')}
              </Button>
            ))}
          </div>

          <div className="border border-gray-200 dark:border-slate-800 rounded-lg p-5 bg-slate-50 dark:bg-slate-950/60">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Order Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600 dark:text-gray-400">Credits to purchase</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {formatWalletCurrency(numericAmount, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600 dark:text-gray-400">GST (18%)</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {formatWalletCurrency(gstAmount, { minimumFractionDigits: 0, maximumFractionDigits: 0, withRupee: true })}
                </span>
              </div>
              <div className="pt-3 border-t border-gray-200 dark:border-slate-800 mt-3">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-gray-900 dark:text-white">Amount to pay (incl. GST)</span>
                  <span className="font-bold text-lg text-gray-900 dark:text-white">
                    {formatWalletCurrency(totalDue, { minimumFractionDigits: 0, maximumFractionDigits: 0, withRupee: true })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <Button
            className={cn(waBtn.primary, 'w-full h-12 text-base')}
            disabled={!isValid || isProcessing}
            onClick={handlePay}
          >
            {isProcessing
              ? 'Processing…'
              : `Pay ${formatWalletCurrency(totalDue, { minimumFractionDigits: 0, maximumFractionDigits: 0, withRupee: true })}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
